import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/libs/utils";

/**
 * 뷰포트에 들어오면 처음부터 재생, 벗어나면 정지하는 영상 (#5415).
 *
 * ## 왜 공유로 올렸나
 *
 * 같은 동작이 세 곳에서 필요해졌다:
 *
 * ```
 *   ath/PlayGameHapticsOn.tsx   FeatureVideo   ← AI Powered 페이지 (동결)
 *   pc-overview/PcAiBand.tsx    BandVideo      ← 사본 1
 *   pc-software/PcSoftwareConnect.tsx          ← 여기서 세 번째가 될 뻔했다
 * ```
 *
 * `PcAiBand` 가 사본을 두면서 *"둘 중 하나만 고치는 일이 생긴다 — 재생 동작을 바꿀 일이
 * 있으면 두 파일을 함께 볼 것"* 이라 적어 뒀다. 세 번째 소비처가 생긴 시점이 그 경고를
 * 실행할 자리라, 사본을 늘리는 대신 여기로 올린다.
 *
 * ⚠️ **`ath/PlayGameHapticsOn.tsx` 는 그대로 둔다.** 그 파일은 AI Powered 페이지 전용으로
 * 동결돼 있어(그 페이지를 건드리지 않는 것이 #5407 이후 작업의 전제) 이 리팩터의 대상이
 * 아니다. 그래서 사본은 **2벌**로 남는다 — 0벌이 아니다. 동결이 풀리면 그쪽도 여기를
 * 쓰게 하는 것이 다음 단계다.
 *
 * ## 동작
 *
 * - `muted` — 자동재생 정책을 통과하려면 필수다. 소리가 필요하면 이 컴포넌트를 쓸 수 없다
 * - `preload="none"` — CDN 영상이라 화면 밖 것을 미리 받지 않는다
 * - 첫 프레임 디코드(`onLoadedData`) 전까지 pulse 스켈레톤 — 그게 없으면 진입 후
 *   네트워크 갭 동안 **빈 상자**로 보인다
 *
 * ## `poster` — 스켈레톤 대신 **실제 이미지**를 깐다 (2026-09-11)
 *
 * 스켈레톤은 `bg-white/50` 이라 **어두운 배경에서 흰 판**으로 번쩍인다. 그 영상의
 * 정지 컷이 이미 있는 자리라면 그걸 까는 편이 낫다 — 로딩 중에도 **무엇이 나올지**
 * 보인다.
 *
 * ⚠️ `poster` 를 주면 **opacity 페이드를 걸지 않는다.** 포스터는 `<video>` 자신이
 * 그리므로 `opacity-0` 이면 **포스터까지 같이 사라진다** — 그러면 스켈레톤도 없고
 * 포스터도 없는 빈 상자가 된다. 포스터→첫 프레임 전환은 브라우저가 알아서 하므로
 * 페이드가 필요 없다.
 *
 * ⚠️ `preload="none"` 이어도 **포스터 이미지는 받는다**(영상 데이터만 미룬다).
 * 그래서 화면 밖 카드도 포스터 한 장은 내려온다 — 정지 컷이 가벼운 자리에만 쓴다.
 *
 * ## `respectReducedMotion` · `ariaLabel` — 쿨링 팬 PDP 에서 추가 (2026-10-01 · #5661)
 *
 * 쿨링 팬 STEP 03 영상이 이 컴포넌트의 사본(`CoolingFanVideo`)으로 들어갔다가 리뷰(R5 1번)로
 * 여기로 합쳤다. 사본에만 있던 두 가지를 **옵트인**으로 올렸다:
 *
 * - `respectReducedMotion` — `prefers-reduced-motion: reduce` 면 재생하지 않고 포스터만 남긴다.
 * - `ariaLabel` — 영상에 접근성 이름을 붙인다(이미지 자리의 `alt` 에 해당).
 *
 * ⚠️ **기본값은 둘 다 꺼짐**이다 — 기존 소비처 5곳(AudioToHapticDialog · gta5-feel-cards ·
 * gta5-step-into-game · PcAiBand · PcSoftwareConnect)의 동작을 이 PR 에서 바꾸지 않기 위해서다.
 * reduced-motion 을 기본으로 켜는 것과 WCAG 2.2.2(5초 넘는 반복 영상의 정지 수단)는 **리포
 * 공통 과제**로 따로 다룬다.
 */
export default function ViewportVideo({
  src,
  loop,
  poster,
  respectReducedMotion = false,
  ariaLabel,
  className,
}: {
  src: string;
  /** 짧은 클립이면 보이는 동안 반복. 긴 영상은 한 번만. */
  loop?: boolean;
  /**
   * 첫 프레임 전에 깔 정지 컷. 주면 **스켈레톤과 페이드를 둘 다 끈다**
   * (위 docblock 참조 — 페이드를 남기면 포스터까지 투명해진다).
   */
  poster?: string;
  /** `prefers-reduced-motion: reduce` 면 재생하지 않는다(포스터만). 기본 꺼짐 — 위 docblock. */
  respectReducedMotion?: boolean;
  /** 영상의 접근성 이름. 없으면 이름 없는 영상이다(종전 동작). */
  ariaLabel?: string;
  className?: string;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (
      respectReducedMotion &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.currentTime = 0;
          el.play().catch(() => {}); // 자동재생 차단 등은 무시
        } else {
          el.pause();
        }
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [respectReducedMotion]);

  return (
    <>
      {!poster && !loaded && (
        <div
          className="absolute inset-0 animate-pulse bg-white/50"
          aria-hidden
        />
      )}
      <video
        ref={ref}
        src={src}
        poster={poster}
        muted
        loop={loop}
        playsInline
        preload="none"
        aria-label={ariaLabel}
        onLoadedData={() => setLoaded(true)}
        className={cn(
          "size-full object-cover",
          !poster && "transition-opacity duration-300",
          !poster && !loaded && "opacity-0",
          className,
        )}
      />
    </>
  );
}
